import { Suspense, lazy, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import type { Session } from '@supabase/supabase-js'

import { supabase } from './lib/supabase'
import { apiGet } from './lib/api'
import type { Me } from './lib/types'
import Layout from './components/Layout'
import Login from './screens/Login'
import Onboarding from './screens/Onboarding'
import Catalog from './screens/Catalog'
import Theory from './screens/Theory'
import TaskScreen from './screens/Task'
import ProgressScreen from './screens/Progress'
import Achievements from './screens/Achievements'
import Help from './screens/Help'
import History from './screens/History'
import Rules from './screens/Rules'
import Lab from './screens/Lab'

// песочница грузит движок (~3 МБ WebAssembly) — только когда её открыли
const Pesochnica = lazy(() => import('./screens/Pesochnica'))
const песочница = (
  <Suspense fallback={<div className="center muted">Загружаем песочницу…</div>}>
    <Pesochnica />
  </Suspense>
)
// симуляции «Лаборатории» — тем же отдельным куском, что и песочница
const Simulyaciya = lazy(() => import('./screens/Simulyaciya'))
const симуляция = (
  <Suspense fallback={<div className="center muted">Загружаем симуляцию…</div>}>
    <Simulyaciya />
  </Suspense>
)

export default function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [checked, setChecked] = useState(false)
  const location = useLocation()

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setChecked(true)
    })
    const { data } = supabase.auth.onAuthStateChange((_e, s) => setSession(s))
    return () => data.subscription.unsubscribe()
  }, [])

  if (!checked) return <div className="app-main center muted">Загружаем…</div>
  // Лаборатория открывается и без входа: бот ведёт сюда кнопкой мини-приложения Telegram
  if (!session && location.pathname.startsWith('/lab')) {
    return (
      <Layout>
        <Routes>
          <Route path="/lab/pesochnica" element={песочница} />
          <Route path="/lab/sim/:sim/:ekran?" element={симуляция} />
          <Route path="/lab/:opyt?" element={<Lab loggedIn={false} />} />
        </Routes>
      </Layout>
    )
  }
  if (!session) return <Login />

  return <Authorized />
}

function Authorized() {
  const { data: me, isLoading, error } = useQuery<Me>({
    queryKey: ['me'],
    queryFn: () => apiGet<Me>('/api/me'),
  })

  if (isLoading) return <div className="app-main center muted">Загружаем…</div>
  if (error || !me) {
    return (
      <div className="app-main">
        <div className="error">
          Не удалось получить профиль. Проверь связь и обнови страницу.
        </div>
        <button className="btn" onClick={() => supabase.auth.signOut()}>Выйти</button>
      </div>
    )
  }

  // Экзамен не выбран — спрашиваем прямо, до всего остального. От него зависят
  // и цель, и оценка балла, и подбор задач; молчаливое умолчание отправило бы
  // одиннадцатиклассника готовиться к ОГЭ.
  if (!me.exam.chosen) return <Onboarding me={me} />

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Catalog me={me} />} />
        <Route path="/theory/:nodeId" element={<Theory />} />
        <Route path="/task" element={<TaskScreen me={me} />} />
        <Route path="/history/:topicId" element={<History />} />
        <Route path="/progress" element={<ProgressScreen me={me} />} />
        <Route path="/achievements" element={<Achievements me={me} />} />
        <Route path="/help" element={<Help />} />
        <Route path="/rules" element={<Rules />} />
        {/* Лаборатория — исключение из режима показа (§2.16): работает без API, ученику не мешает. */}
        <Route path="/lab/pesochnica" element={песочница} />
        <Route path="/lab/sim/:sim/:ekran?" element={симуляция} />
        <Route path="/lab/:opyt?" element={<Lab />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  )
}
